import React, { useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface CopilotProfile {
  id: string;
  name: string;
}

interface EnhancedCopilotInputProps {
  copilots: string[];
  setCopilots: (copilots: string[]) => void;
  availableCopilots: CopilotProfile[];
  disabled?: boolean;
  maxDisplayCount?: number;
}

export const EnhancedCopilotInput = ({
  copilots,
  setCopilots,
  availableCopilots,
  disabled = false,
  maxDisplayCount = 3
}: EnhancedCopilotInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const displayCopilots = copilots.slice(0, maxDisplayCount);
  const remainingCount = copilots.length - maxDisplayCount;
  
  const availableCopilotsToShow = availableCopilots.filter(copilot => !copilots.includes(copilot.id));

  const handleAddCopilot = (copilotId: string) => {
    if (!copilots.includes(copilotId)) {
      setCopilots([...copilots, copilotId]);
    }
  };

  const handleRemoveCopilot = (copilotIdToRemove: string) => {
    setCopilots(copilots.filter(copilotId => copilotId !== copilotIdToRemove));
  };

  const getCopilotName = (copilotId: string) => {
    const copilot = availableCopilots.find(c => c.id === copilotId);
    return copilot?.name || copilotId;
  };

  return (
    <div className="space-y-2">
      {/* Display selected copilots */}
      {copilots.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {displayCopilots.map((copilotId, index) => (
            <Badge 
              key={index} 
              variant="secondary"
              className="flex items-center gap-1 py-1 pl-2 pr-1"
            >
              <Sparkles className="h-3 w-3 opacity-70" />
              {getCopilotName(copilotId)}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveCopilot(copilotId)}
                  className="rounded-full hover:bg-muted focus:outline-none"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
          
          {remainingCount > 0 && (
            <Badge variant="outline" className="flex items-center gap-1 py-1">
              +{remainingCount} more
            </Badge>
          )}
        </div>
      )}
      
      {/* Copilot selector */}
      <Select 
        open={isOpen} 
        onOpenChange={setIsOpen}
        onValueChange={(value) => {
          if (value) {
            handleAddCopilot(value);
            // Reset the select value so the same copilot can be selected again if needed
            setTimeout(() => {
              const selectElement = document.querySelector('[data-radix-select-trigger]') as HTMLButtonElement;
              if (selectElement) {
                selectElement.click();
              }
            }, 100);
          }
        }}
        disabled={disabled || availableCopilotsToShow.length === 0}
      >
        <SelectTrigger className="w-full focus:ring-primary/30 focus:border-primary">
          <SelectValue placeholder={availableCopilotsToShow.length === 0 ? "No copilots available" : "Add copilots..."} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {availableCopilotsToShow.length > 0 && (
              availableCopilotsToShow.map((copilot) => (
                <SelectItem key={copilot.id} value={copilot.id}>
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-muted-foreground" />
                    <span>{copilot.name}</span>
                  </div>
                </SelectItem>
              ))
            )}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}; 